/**
 * What has gone into a car.
 *
 * One row per expense. The vehicle's total cost is the purchase price plus
 * every line that has not been cancelled, worked out by the database — so
 * nothing here writes a total, and nothing can disagree with the lines.
 */

import { supabase } from "../../lib/supabase";
import { nothingChanged } from "../../lib/saving";

export type ExpenseCategory =
  | "transport" | "parts" | "mechanical" | "body_paint"
  | "tires_wheels" | "brakes" | "detail" | "maintenance" | "other";

export type ExpenseState = "ordered" | "awaiting_install" | "completed" | "cancelled";

export interface VehicleExpense {
  id: string;
  vehicle_id: string;
  incurred_on: string;
  category: ExpenseCategory;
  state: ExpenseState;
  description: string;
  vendor: string | null;
  invoice_no: string | null;
  amount_cents: number;
  notes: string | null;
}

export const CATEGORIES: { value: ExpenseCategory; label: string }[] = [
  { value: "transport",    label: "Transport" },
  { value: "parts",        label: "Parts" },
  { value: "mechanical",   label: "Mechanical" },
  { value: "body_paint",   label: "Body and paint" },
  { value: "tires_wheels", label: "Tires and wheels" },
  { value: "brakes",       label: "Brakes" },
  { value: "detail",       label: "Detail" },
  { value: "maintenance",  label: "Maintenance" },
  { value: "other",        label: "Other" },
];

export const CATEGORY_LABEL: Record<string, string> =
  Object.fromEntries(CATEGORIES.map((c) => [c.value, c.label]));

export const STATES: { value: ExpenseState; label: string }[] = [
  { value: "ordered",          label: "Ordered" },
  { value: "awaiting_install", label: "Delivered, awaiting install" },
  { value: "completed",        label: "Completed" },
  // Not one of the three you named, and kept deliberately: it is the only way
  // to take a job off a car's cost without deleting the fact it was there.
  { value: "cancelled",        label: "Cancelled" },
];

/** Vendors bought from often enough to be worth one tap. */
export const VENDORS = [
  "eBay", "Amazon", "Partsmax", "KSI", "OEM Dealer",
] as const;

export const STATE_LABEL: Record<string, string> =
  Object.fromEntries(STATES.map((s) => [s.value, s.label]));

/** Ordered and waiting lines are committed money on work that is not finished.
    A car with any of them is not fully costed yet. */
export const isOpen = (e: VehicleExpense) =>
  e.state === "ordered" || e.state === "awaiting_install";

/** Everything except cancelled counts toward the car. */
export const counts = (e: VehicleExpense) => e.state !== "cancelled";

export interface CostSummary {
  purchase: number;
  expenses: number;
  total: number;
  openLines: number;
  openCents: number;
}

export function summarise(purchase: number, rows: VehicleExpense[]): CostSummary {
  const counted = rows.filter(counts);
  const open = rows.filter(isOpen);
  const expenses = counted.reduce((n, e) => n + e.amount_cents, 0);
  return {
    purchase,
    expenses,
    total: purchase + expenses,
    openLines: open.length,
    openCents: open.reduce((n, e) => n + e.amount_cents, 0),
  };
}

/* ------------------------------------------------------------------ */
/* Reading and writing                                                 */
/* ------------------------------------------------------------------ */

export async function listExpenses(vehicleId: string): Promise<VehicleExpense[]> {
  const { data, error } = await supabase
    .from("vehicle_expenses")
    .select("*")
    .eq("vehicle_id", vehicleId)
    .order("incurred_on", { ascending: false })
    .order("created_at", { ascending: false });
  if (error) throw new Error(`Could not read the expenses: ${error.message}`);
  return (data ?? []) as VehicleExpense[];
}

export type ExpenseDraft = Omit<VehicleExpense, "id" | "vehicle_id">;

function describeError(message: string): string {
  if (/expense_has_description/.test(message)) {
    return "Say what the money went on.";
  }
  if (/expense_amount_nonneg/.test(message)) {
    return "An expense cannot be negative. To undo one, cancel it.";
  }
  return message;
}

export async function addExpense(vehicleId: string, draft: ExpenseDraft) {
  const { error } = await supabase
    .from("vehicle_expenses")
    .insert({ ...draft, vehicle_id: vehicleId });
  if (error) throw new Error(`Could not add it: ${describeError(error.message)}`);
}

export async function updateExpense(id: string, draft: Partial<ExpenseDraft>) {
  const { data, error } = await supabase
    .from("vehicle_expenses").update(draft).eq("id", id).select("id");
  if (error) throw new Error(`Could not save it: ${describeError(error.message)}`);
  if (!data?.length) throw nothingChanged();
}

/**
 * Removing a line entirely.
 *
 * For something entered against the wrong car or typed twice. A job that was
 * called off should be cancelled instead — that keeps it visible, which is
 * what anyone looking at the car's history later actually wants.
 */
export async function deleteExpense(id: string) {
  const { data, error } = await supabase.from("vehicle_expenses").delete().eq("id", id).select("id");
  if (error) throw new Error(`Could not remove it: ${error.message}`);
  if (!data?.length) throw nothingChanged();
}
